/**
 * FP3-037 correction — Super Admin "Add School / Campus" modal.
 *
 * Calls POST /api/admin/schools which already exists. Super admin
 * can target any organization; org admins (who never see this modal)
 * stay scoped server-side.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Buildings } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

const ACCESS_TYPES = [
  { value: "inherited", label: "Inherited from org" },
  { value: "trial", label: "Trial" },
  { value: "pilot", label: "Pilot" },
  { value: "limited", label: "Limited" },
  { value: "membership", label: "Full membership" },
];

const MEMBERSHIP_STATUSES = [
  { value: "inherited", label: "Inherited" },
  { value: "trialing", label: "Trialing" },
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
  { value: "inactive", label: "Inactive" },
];

export default function AddSchoolModal({
  open,
  onClose,
  onCreated,
  organizations = [],
  prefillOrganizationId = "",
}) {
  const [submitting, setSubmitting] = useState(false);
  const [orgId, setOrgId] = useState(prefillOrganizationId);
  const [name, setName] = useState("");
  const [accessType, setAccessType] = useState("inherited");
  const [membershipStatus, setMembershipStatus] = useState("inherited");
  const [emailDomain, setEmailDomain] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setOrgId(prefillOrganizationId || (organizations[0]?.id || ""));
    setName("");
    setAccessType("inherited");
    setMembershipStatus("inherited");
    setEmailDomain("");
    setNotes("");
  }, [open, prefillOrganizationId, organizations]);

  const canSubmit = !!orgId && !!name.trim() && !submitting;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        organization_id: orgId,
        name: name.trim(),
        access_type: accessType,
        membership_status: membershipStatus,
        billing_interval: "inherited",
        email_domain: emailDomain.trim().toLowerCase().replace(/^@/, "") || null,
      };
      const res = await api.createAdminSchool(payload);
      toast.success(`Campus "${name.trim()}" added`);
      onCreated?.(res?.data);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        (typeof detail === "string" && detail) || "Could not create campus"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="add-school-modal"
        className="max-w-md max-h-[90vh] overflow-y-auto p-0 border-[#E5E3DB] rounded-2xl"
      >
        <div className="px-5 py-4 border-b border-[#E5E3DB] flex items-center gap-2.5 bg-[#FBFAF6]">
          <div className="w-8 h-8 rounded-lg bg-[#1F4F73] flex items-center justify-center">
            <Buildings size={16} weight="duotone" className="text-white" />
          </div>
          <div>
            <DialogTitle className="text-[15px] font-semibold text-[#1F2937] heading-font">
              Add school / campus
            </DialogTitle>
            <DialogDescription className="text-[12px] text-[#6B7280] mt-0.5">
              Adds a campus under an existing organization. Access posture defaults to inherited.
            </DialogDescription>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <label className="block">
            <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">Organization *</span>
            <select
              data-testid="add-school-org"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              disabled={!!prefillOrganizationId}
              className="w-full text-[13px] px-3 py-2 rounded-md border border-[#E5E3DB] focus:outline-none focus:ring-2 focus:ring-[#1F4F73]/30 focus:border-[#1F4F73] bg-white disabled:bg-[#F3F1EC]"
            >
              <option value="">Select organization…</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || o.id}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">Campus name *</span>
            <Input
              data-testid="add-school-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="e.g., Lincoln Elementary"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">Access type</span>
              <select
                data-testid="add-school-access-type"
                value={accessType}
                onChange={(e) => setAccessType(e.target.value)}
                className="w-full text-[13px] px-3 py-2 rounded-md border border-[#E5E3DB] focus:outline-none focus:ring-2 focus:ring-[#1F4F73]/30 focus:border-[#1F4F73] bg-white"
              >
                {ACCESS_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">Membership</span>
              <select
                data-testid="add-school-membership-status"
                value={membershipStatus}
                onChange={(e) => setMembershipStatus(e.target.value)}
                className="w-full text-[13px] px-3 py-2 rounded-md border border-[#E5E3DB] focus:outline-none focus:ring-2 focus:ring-[#1F4F73]/30 focus:border-[#1F4F73] bg-white"
              >
                {MEMBERSHIP_STATUSES.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <label className="block">
            <span className="block text-[11px] font-semibold text-[#3F4756] mb-1">
              Staff email domain (optional)
            </span>
            <Input
              data-testid="add-school-email-domain"
              value={emailDomain}
              onChange={(e) => setEmailDomain(e.target.value)}
              placeholder="lincoln.k12.us"
            />
            <span className="text-[10.5px] text-[#9AA0A6] mt-1 block">
              Speeds up invite entry — when set, partial usernames auto-complete to user@{(emailDomain || "domain").replace(/^@/, "")}.
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              data-testid="add-school-cancel"
              variant="outline"
              onClick={() => !submitting && onClose?.()}
              className="border-[#E5E3DB]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="add-school-submit"
              disabled={!canSubmit}
              className="bg-[#1F4F73] hover:bg-[#143D5B] text-white"
            >
              {submitting ? "Creating…" : "Create campus"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
